import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { InitPayload } from '@/types/messaging.types';
import type { UserDetails } from '@/types/user.types';
import { careplyxService } from '@/services/CareplyxService';
import { fingerprintService } from '@/services/FingerprintService';

interface AppConfigState {
  apiKey: string;
  employeeId: string;
  scanToken: string | null;
  scanTokenExpiresAt: number;
  isInitialized: boolean;
  prefill: Partial<UserDetails> | null;
}

interface AppConfigValue extends AppConfigState {
  initFromPayload: (payload: InitPayload) => void;
  setScanToken: (token: string, expiresAt: number) => void;
  resetConfig: () => void;
}

const defaultApiKey = import.meta.env.VITE_CAREPLIX_SDK_KEY ?? '';

const initialState: AppConfigState = {
  apiKey: defaultApiKey,
  employeeId: 'guest',
  scanToken: null,
  scanTokenExpiresAt: 0,
  isInitialized: false,
  prefill: null,
};

const AppConfigContext = createContext<AppConfigValue | null>(null);

export function AppConfigProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [state, setState] = useState<AppConfigState>(initialState);

  const initFromPayload = useCallback((payload: InitPayload) => {
    const employeeId = payload.userId ?? 'guest';
    careplyxService.setCredentials(payload.apiKey, employeeId);
    fingerprintService.setEmployeeId(employeeId);
    setState((prev) => ({
      ...prev,
      apiKey: payload.apiKey,
      employeeId,
      prefill: payload.prefill ?? null,
      isInitialized: true,
    }));
  }, []);

  const setScanToken = useCallback((token: string, expiresAt: number) => {
    careplyxService.setScanToken(token, expiresAt);
    setState((prev) => ({
      ...prev,
      scanToken: token,
      scanTokenExpiresAt: expiresAt,
    }));
  }, []);

  const resetConfig = useCallback(() => {
    setState(initialState);
  }, []);

  const value = useMemo(
    () => ({ ...state, initFromPayload, setScanToken, resetConfig }),
    [state, initFromPayload, setScanToken, resetConfig],
  );

  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>;
}

export function useAppConfig(): AppConfigValue {
  const ctx = useContext(AppConfigContext);
  if (!ctx) throw new Error('useAppConfig must be used within AppConfigProvider');
  return ctx;
}
